import { ReactElement, useEffect, useState } from "react";
import { NextPageContext, InferGetServerSidePropsType } from "next";
import ProductLevelZero from "@containers/ProductLevelZero";
import { getCatalogProductLevel } from "@libraries/api/product-level/product.repositories";
import { getProductMenu } from "@libraries/api/product-menu/product-menu.repos";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import LandingPageLayout from "@layouts/LandingPage";
import type { NextPageWithLayout } from "../_app";
import { localeKey } from "@constants/locale-key";

const PageProductLevelZero: NextPageWithLayout<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({ baseUrl, data, dataNavbar }) => {
  const [content, setContent] = useState<any>({});
  const getContentMessage = (e: MessageEvent) => {
    setContent(e.data);
  };

  useEffect(() => {
    if (content.id) {
      window.addEventListener("message", getContentMessage);
      return () => {
        window.removeEventListener("message", getContentMessage);
      };
    }
  }, []);

  return (
    <ProductLevelZero
      baseUrl={baseUrl}
      data={content.id ? content : data}
      dataNavbar={dataNavbar}
    />
  );
};

PageProductLevelZero.getLayout = (page: ReactElement) => {
  return (
    <LandingPageLayout
      footer={{
        dataNavbar: page.props?.dataNavbar,
        bgColor: "white",
      }}
      hideBottomNav={true}
    >
      {page}
    </LandingPageLayout>
  );
};

export async function getServerSideProps(context: NextPageContext) {
  const language = `${context.locale}`;
  const slug = `${context.query.level_zero}`;

  context?.res?.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  );

  const response = await getCatalogProductLevel(slug, language);
  const responseProductMenu = await getProductMenu({ level: 0 }, language);

  if (!response) {
    return {
      redirect: {
        destination: `/404`,
        permanent: false,
      },
    };
  }

  const { data: dataProductMenu } = JSON.parse(JSON.stringify(responseProductMenu));
  const { data } = JSON.parse(JSON.stringify(response));

  return {
    props: {
      title: data.metaTitle || null,
      description: data.metaDescription || null,
      imageUrl: data.productImage?.mediaPath || "",
      baseUrl: `//${context?.req?.headers.host}`,
      canonical: data.canonical,
      data,
      dataNavbar: dataProductMenu,
      ...(await serverSideTranslations(context.locale ?? "id", localeKey)),
    },
  };
}

export default PageProductLevelZero;
